// Retail Orders — card-reader payment recovery (Sep 2026).
//
// Before this, a card-reader payment that nobody finished (the customer
// walked away, changed their mind, or the reader dropped) had no staff
// control at all: the order sat "pending", could not be discounted or
// retried, and the reader stayed blocked.
//
// Everything here is the SERVER's decision. "Cancel card payment" uses
// the same cancellation the kiosk uses: before a reader picks the payment
// up it is closed at once; after that the reader is asked to stop and
// reports what actually happened, so a card that was already accepted
// still completes. "Check payment with Stripe" is the existing
// reconciliation, offered only when the reader never acknowledged a
// cancel (its agent is not running) or the outcome is uncertain — it
// reads the truth from Stripe and never charges anyone.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "../../../lib/api";

const IN_FLIGHT = new Set(["created", "awaiting_device", "claimed", "payment_in_progress"]);
const RECONCILIATION = "reconciliation_required";
// Payment states in which a reader attempt can still need staff.
const RELEVANT_PAYMENT_STATUSES = new Set(["", "pending", "reconciliation_required"]);
const POLL_MS = 4000;

const STATUS_LABEL = {
  created: "Preparing",
  awaiting_device: "Waiting for the reader",
  claimed: "Reader is waiting for a card",
  payment_in_progress: "Reader is waiting for a card",
  reconciliation_required: "Outcome uncertain — check with Stripe",
};

function errorMessage(e, fallback) {
  const det = e?.response?.data?.detail;
  if (det && typeof det === "object" && det.message) return det.message;
  if (typeof det === "string") return det;
  return fallback;
}

export default function StripeTerminalRecoveryPanel({ order, onChanged }) {
  const orderId = order?.id;
  const paymentStatus = String(order?.payment_status || "").toLowerCase();
  const relevant = !!orderId && RELEVANT_PAYMENT_STATUSES.has(paymentStatus);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    if (!relevant) { setSession(null); return; }
    try {
      const { data } = await api.get(`/pos/stripe/terminal/orders/${orderId}/session`);
      setSession(data?.session?.exists ? data.session : null);
    } catch (_e) {
      // Not a reader tenant, or not an admin: nothing to offer.
      setSession(null);
    }
  }, [orderId, relevant]);

  useEffect(() => { load(); }, [load]);

  const inFlight = !!session && IN_FLIGHT.has(session.status);
  const waitingOnReader = inFlight && !!session.cancel_requested && !session.cancel_stalled;

  // While the reader is being asked to stop, follow it until it answers.
  useEffect(() => {
    if (!waitingOnReader) return undefined;
    const t = setTimeout(load, POLL_MS);
    return () => clearTimeout(t);
  }, [waitingOnReader, session, load]);

  if (!session) return null;
  const needsReconcile =
    session.status === RECONCILIATION || (inFlight && !!session.cancel_stalled);
  if (!inFlight && !needsReconcile) return null;

  const cancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pos/stripe/terminal/sessions/${session.session_id}/cancel`);
      setSession(data?.session || session);
      toast.success(
        data?.cancel_requested
          ? "The reader has been asked to stop. This updates when it responds."
          : "Card payment cancelled. The order is unpaid."
      );
      setConfirming(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(errorMessage(e, "Could not cancel the card payment."));
      load();
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pos/stripe/terminal/sessions/${session.session_id}/reconcile`);
      setSession(data?.session || null);
      toast.success(
        data?.session?.status === "succeeded"
          ? "Stripe confirmed the payment. The order is paid."
          : "Stripe confirmed nothing was charged. The attempt is closed."
      );
      onChanged && onChanged();
    } catch (e) {
      toast.error(errorMessage(e, "Could not check the payment with Stripe."));
      load();
    } finally {
      setBusy(false);
    }
  };

  const amount = Number(session.amount_cents || 0) / 100;

  return (
    <div
      className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
      data-testid="terminal-recovery-panel"
    >
      <div className="font-medium text-stone-900">Card reader payment</div>
      <div className="mt-1 text-stone-700" data-testid="terminal-recovery-status">
        {STATUS_LABEL[session.status] || session.status} · ${amount.toFixed(2)}
      </div>

      {waitingOnReader && (
        <div className="mt-2 flex items-center gap-2 text-stone-700" data-testid="terminal-recovery-waiting">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cancellation requested — waiting for the reader to stop.
        </div>
      )}

      {inFlight && !session.cancel_requested && !confirming && (
        <button
          type="button"
          className="mt-2 rounded-md border border-stone-300 bg-white px-3 py-1.5 font-medium hover:bg-stone-50"
          onClick={() => setConfirming(true)}
          data-testid="terminal-recovery-cancel"
        >
          Cancel card payment
        </button>
      )}
      {inFlight && !session.cancel_requested && confirming && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-stone-700">Stop this payment on the reader?</span>
          <button
            type="button"
            disabled={busy}
            className="rounded-md bg-red-600 px-3 py-1.5 font-medium text-white disabled:opacity-60"
            onClick={cancel}
            data-testid="terminal-recovery-cancel-confirm"
          >
            {busy ? "Cancelling…" : "Yes, cancel"}
          </button>
          <button
            type="button"
            disabled={busy}
            className="rounded-md border border-stone-300 bg-white px-3 py-1.5"
            onClick={() => setConfirming(false)}
          >
            Keep waiting
          </button>
        </div>
      )}

      {needsReconcile && (
        <div className="mt-2">
          <div className="text-stone-700">
            {session.status === RECONCILIATION
              ? "This payment's outcome is uncertain. Do not take another payment until it is checked."
              : "The reader has not responded to the cancellation. Check the payment with Stripe instead of waiting."}
          </div>
          <button
            type="button"
            disabled={busy}
            className="mt-2 rounded-md border border-stone-300 bg-white px-3 py-1.5 font-medium hover:bg-stone-50 disabled:opacity-60"
            onClick={reconcile}
            data-testid="terminal-recovery-reconcile"
          >
            {busy ? "Checking…" : "Check payment with Stripe"}
          </button>
        </div>
      )}
    </div>
  );
}
